import type Keycloak from 'keycloak-js';

const PROVIDER_KEY = 'kds.auth.provider';

export class KeycloakSessionEndedError extends Error {
  constructor() {
    super('Keycloak session has ended');
    this.name = 'KeycloakSessionEndedError';
  }
}

let session: Promise<Keycloak> | undefined;
let initialization: Promise<boolean> | undefined;

export const usesKeycloak = () => {
  if (globalThis.window === undefined) {
    return false;
  }
  return sessionStorage.getItem(PROVIDER_KEY) === 'keycloak';
};
export const selectCognitoLogin = () => sessionStorage.removeItem(PROVIDER_KEY);

export const keycloakSession = (): Promise<Keycloak> => {
  session ??= import('keycloak-js')
    .then(({ default: KeycloakClient }) => new KeycloakClient({ url: 'https://auth.sh.calo.app', realm: 'hub', clientId: 'kds' }))
    .catch((error: unknown) => {
      session = undefined;
      throw error;
    });
  return session;
};

export const initializeKeycloak = async () => {
  const client = await keycloakSession();
  initialization ??= client.init({
    ...(usesKeycloak() ? { onLoad: 'check-sso' as const } : {}),
    flow: 'standard',
    pkceMethod: 'S256',
    useNonce: true,
    responseMode: 'fragment',
    checkLoginIframe: false,
    redirectUri: `${location.origin}/`,
    scope: 'openid profile email kds-access'
  });
  return await initialization;
};

export const signInKeycloak = async () => {
  try {
    await initializeKeycloak();
  } catch {
    sessionStorage.setItem(PROVIDER_KEY, 'keycloak');
    globalThis.location.reload();
    return;
  }
  const client = await keycloakSession();
  sessionStorage.setItem(PROVIDER_KEY, 'keycloak');
  try {
    await client.login({ redirectUri: `${location.origin}/` });
  } catch (error) {
    selectCognitoLogin();
    throw error;
  }
};

export const keycloakAccessToken = async (): Promise<string> => {
  if (!usesKeycloak()) {
    throw new Error('No Keycloak session');
  }
  const initialized = await initializeKeycloak();
  if (!initialized) {
    throw new KeycloakSessionEndedError();
  }
  const client = await keycloakSession();
  await client.updateToken();
  if (!usesKeycloak() || !client.authenticated || !client.token) {
    throw new KeycloakSessionEndedError();
  }
  return client.token;
};
